import { supabase, run } from './db';

/**
 * Dados dos relatórios.
 *
 * Sem `cached`: relatório é para decidir, e mostrar cópia local sem avisar
 * faria alguém concluir coisa errada sobre a produção. Sem rede, falha.
 */

export const listarProducaoPorDoadora = ({ laboratoryId, de, ate, clientId }) => {
  let q = supabase
    .from('vw_producao_por_doadora')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('service_date', { ascending: false });

  if (de) q = q.gte('service_date', de);
  if (ate) q = q.lte('service_date', ate);
  if (clientId) q = q.eq('client_id', clientId);

  return run(q);
};

/**
 * Taxa em porcentagem, ou `null` quando não há denominador.
 *
 * `null` e `0` são coisas diferentes aqui: zero por cento é um resultado ruim,
 * "sem denominador" é ausência de informação. A tela mostra "—" para o segundo,
 * e apresentar os dois como 0% esconderia doadora que nem chegou a ser aspirada.
 */
export const taxa = (parte, total) => {
  const p = Number(parte) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return null;
  return Math.round((p / t) * 1000) / 10;
};

/** Soma uma coluna do conjunto filtrado. */
const somar = (linhas, campo) =>
  linhas.reduce((s, l) => s + (Number(l[campo]) || 0), 0);

/**
 * O funil inteiro, do oócito à prenhez.
 *
 * Cada etapa traz o total e a taxa em relação à ANTERIOR, que é a pergunta
 * real ("de cada 10 viáveis, quantos clivaram?"). Taxa sobre o topo do funil
 * responderia outra coisa e esconderia onde a perda acontece.
 */
export const funilDeProducao = (linhas = []) => {
  const oocitos     = somar(linhas, 'oocitos');
  const viaveis     = somar(linhas, 'viaveis');
  const clivados    = somar(linhas, 'clivados');
  const embrioes    = somar(linhas, 'embrioes');
  const transferidos = somar(linhas, 'transferidos');
  const prenhes     = somar(linhas, 'prenhes_dg30');
  // Mesmo denominador honesto do Dashboard: conferidas, não transferidas.
  const conferidas  = somar(linhas, 'conferidos_dg30');

  return [
    { etapa: 'Oócitos',      valor: oocitos,      taxa: null },
    { etapa: 'Viáveis',      valor: viaveis,      taxa: taxa(viaveis, oocitos) },
    { etapa: 'Clivados',     valor: clivados,     taxa: taxa(clivados, viaveis) },
    { etapa: 'Embriões',     valor: embrioes,     taxa: taxa(embrioes, viaveis) },
    { etapa: 'Transferidos', valor: transferidos, taxa: taxa(transferidos, embrioes) },
    { etapa: 'Prenhes DG30', valor: prenhes,      taxa: taxa(prenhes, conferidas) },
  ];
};

/**
 * Agrupa por uma dimensão (doadora ou touro), somando o funil.
 *
 * A mesma doadora aparece em serviços diferentes; agrupar aqui responde "como
 * ela rende no geral", que é a pergunta de quem escolhe doadora para a próxima
 * temporada.
 */
export const agruparPor = (linhas = [], campo) => {
  const mapa = new Map();

  linhas.forEach(l => {
    const chave = l[campo] || '—';
    const atual = mapa.get(chave) || {
      chave, oocitos: 0, viaveis: 0, clivados: 0,
      embrioes: 0, transferidos: 0, prenhes_dg30: 0, servicos: new Set(),
    };
    atual.oocitos      += Number(l.oocitos) || 0;
    atual.viaveis      += Number(l.viaveis) || 0;
    atual.clivados     += Number(l.clivados) || 0;
    atual.embrioes     += Number(l.embrioes) || 0;
    atual.transferidos += Number(l.transferidos) || 0;
    atual.prenhes_dg30 += Number(l.prenhes_dg30) || 0;
    atual.servicos.add(l.service_id);
    mapa.set(chave, atual);
  });

  return [...mapa.values()]
    .map(g => ({
      ...g,
      servicos: g.servicos.size,
      taxaClivagem: taxa(g.clivados, g.viaveis),
      taxaBlasto: taxa(g.embrioes, g.viaveis),
    }))
    .sort((a, b) => b.embrioes - a.embrioes);
};


/** Serviços do período, com entrega e dinheiro. */
export const listarServicos = ({ laboratoryId, de, ate, clientId }) => {
  let q = supabase
    .from('vw_servicos_resumo')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('service_date', { ascending: false });

  if (de) q = q.gte('service_date', de);
  if (ate) q = q.lte('service_date', ate);
  if (clientId) q = q.eq('client_id', clientId);
  return run(q);
};

/**
 * Receptoras que receberam embrião no período.
 *
 * Uma linha por TRANSFERÊNCIA, não por receptora: a mesma receptora pode receber
 * em duas sessões, e decidir aqui se isso é uma ou duas tiraria a escolha de quem
 * pergunta. Quem conta distinto é o painel.
 *
 * A fonte é a TE e não o DG. `vw_prenhez` só enxerga a receptora já conferida —
 * em produção 4 contra 409 —, e um painel que diz "4 receptoras" ao lado de "402
 * transferidos" parece quebrado.
 */
export const listarReceptoras = ({ laboratoryId, de, ate, clientId }) => {
  let q = supabase
    .from('vw_receptoras_da_te')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('session_date', { ascending: false });

  if (de) q = q.gte('session_date', de);
  if (ate) q = q.lte('session_date', ate);
  if (clientId) q = q.eq('client_id', clientId);
  return run(q);
};

/** Conferências de prenhez do período, com a origem do embrião. */
export const listarPrenhez = ({ laboratoryId, de, ate, clientId }) => {
  let q = supabase
    .from('vw_prenhez')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('session_date', { ascending: false });

  if (de) q = q.gte('session_date', de);
  if (ate) q = q.lte('session_date', ate);
  if (clientId) q = q.eq('client_id', clientId);
  return run(q);
};

/**
 * Os diagnósticos com resultado, para `prenhasDeAgora`. Sem período, porque a
 * pergunta é quem está prenha hoje, e sem cache: número velho faria prometer
 * bezerro de vaca que abortou.
 */
export const listarDiagnosticosDePrenha = ({ laboratoryId, clientId }) => {
  let q = supabase
    .from('vw_diagnosticos_de_prenha')
    .select('*')
    .eq('laboratory_id', laboratoryId);

  if (clientId) q = q.eq('client_id', clientId);
  return run(q);
};

/**
 * Chave de período a partir de uma data.
 *
 * Corta a string em vez de usar `new Date`: a data vem como 'YYYY-MM-DD' e
 * construir um Date a partir dela em fuso negativo volta um dia — o mesmo
 * off-by-one que a agenda já teve.
 */
export const chaveDePeriodo = (data, granularidade) => {
  const iso = String(data || '').slice(0, 10);
  if (!iso) return '—';
  return granularidade === 'mes' ? iso.slice(0, 7) : iso;
};

/** 'YYYY-MM' e 'YYYY-MM-DD' em rótulo de gente. */
export const rotuloDePeriodo = (chave) => {
  if (!chave || chave === '—') return '—';
  const [a, m, d] = chave.split('-');
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun',
                 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const mes = meses[Number(m) - 1] || m;
  return d ? `${d}/${mes}` : `${mes}/${a.slice(2)}`;
};

/**
 * Soma campos por período, em ordem CRONOLÓGICA.
 *
 * Séries de tempo não se ordenam por tamanho: a leitura é a evolução, e
 * embaralhar por valor destruiria justamente o que se quer ver.
 */
export const agruparPorPeriodo = (linhas = [], campoData, granularidade, campos) => {
  const mapa = new Map();

  linhas.forEach(l => {
    const chave = chaveDePeriodo(l[campoData], granularidade);
    const atual = mapa.get(chave) || { chave, ...Object.fromEntries(campos.map(c => [c, 0])) };
    campos.forEach(c => { atual[c] += Number(l[c]) || 0; });
    atual.linhas = (atual.linhas || 0) + 1;
    mapa.set(chave, atual);
  });

  return [...mapa.values()].sort((a, b) => a.chave.localeCompare(b.chave));
};

/**
 * Resultados de prenhez de um conjunto de conferências.
 *
 * `semResultado` é contado à parte e NÃO entra no denominador: conferência
 * lançada sem resultado é trabalho pela metade, e somá-la como "não prenhe"
 * inventaria um fracasso que ninguém constatou.
 */
export const resumoDePrenhez = (linhas = []) => {
  const prenhes = linhas.filter(l => l.result === 'PRENHE').length;
  const vazias = linhas.filter(l => l.result === 'VAZIA').length;
  const inconclusivas = linhas.filter(l => l.result === 'INCONCLUSIVO').length;
  const abortadas = linhas.filter(l => l.result === 'ABORTOU').length;
  const semResultado = linhas.filter(l => !l.result).length;
  const conferidas = linhas.length - semResultado;

  return {
    total: linhas.length,
    conferidas, prenhes, vazias, inconclusivas, abortadas, semResultado,
    taxa: taxa(prenhes, conferidas),
  };
};

/**
 * Ticket médio: faturado dividido por quem FOI faturado.
 *
 * O denominador é a parte que erra. Dividir pelo total de serviços do período
 * joga no meio da conta todo serviço que ainda não foi faturado — e como o
 * faturamento acontece dias ou semanas depois da execução, o período recente
 * está sempre cheio deles.
 *
 * No dado real do laboratório isso dava 13x de diferença: 13 serviços no
 * período, 1 faturado, R$ 6.435 de faturamento. O painel mostrava R$ 495 como
 * "ticket", que não é o ticket de nada — nenhum serviço foi vendido por esse
 * valor, nem perto.
 *
 * Devolve `faturados` junto para a tela poder dizer sobre quantos a média foi
 * feita. Média sem denominador visível é onde a desconfiança começa.
 */
export const ticketMedio = (linhas = []) => {
  const comFatura = linhas.filter(l => (Number(l.faturado) || 0) > 0);
  const faturado = comFatura.reduce((s, l) => s + (Number(l.faturado) || 0), 0);
  return {
    faturado,
    faturados: comFatura.length,
    ticket: comFatura.length > 0 ? faturado / comFatura.length : 0,
  };
};

/**
 * Os indicadores técnicos, de UMA fonte só.
 *
 * Existia uma segunda conta no Dashboard, somando `services.opu_quantity` e
 * `services.lab_quantity` — os campos denormalizados do serviço. Ela divergia
 * da dos Painéis em tudo: numerador, denominador e nome.
 *
 * Com o dado real do laboratório, os três números que conviviam eram:
 *
 *   Dashboard, rótulo "Taxa de Clivagem"   396 / 1648 = 24,0%
 *   Painel,    "Taxa de Blastocisto"       460 / 1445 = 31,8%
 *   Painel,    "Taxa de Clivagem"                       74,4%
 *
 * O 24% não era clivagem nem blastocisto: era embrião sobre oócito TOTAL, com
 * o nome de outra coisa. Mostrado a um cliente, depreciava o laboratório por
 * um erro de rótulo — a clivagem de verdade é 74,4%.
 *
 * Aqui o grão é a linha da FIV (doadora × touro), que é onde o número nasce.
 * `services.*_quantity` são espelhos mantidos pela tela e por isso derivam:
 * os mesmos serviços davam 396 embriões de um lado e 460 do outro.
 *
 * Toda taxa é sobre a etapa ANTERIOR, e devolve `null` sem denominador — ver
 * `taxa`. Quem exibe precisa tratar o nulo; zero por cento é um resultado, e
 * mostrar zero onde não houve aspiração é afirmar um fracasso que não houve.
 */
export const indicadoresTecnicos = (linhas = []) => {
  const oocitos      = somar(linhas, 'oocitos');
  const viaveis      = somar(linhas, 'viaveis');
  const clivados     = somar(linhas, 'clivados');
  const embrioes     = somar(linhas, 'embrioes');
  const transferidos = somar(linhas, 'transferidos');
  const prenhes30    = somar(linhas, 'prenhes_dg30');
  const prenhes60    = somar(linhas, 'prenhes_dg60');
  const conferidas30 = somar(linhas, 'conferidos_dg30');
  const conferidas60 = somar(linhas, 'conferidos_dg60');

  return {
    oocitos, viaveis, clivados, embrioes, transferidos,
    prenhes30, prenhes60, conferidas30, conferidas60,
    // Viabilidade e clivagem sobre VIÁVEIS, não sobre o total: oócito
    // degenerado nunca foi inseminado, e contá-lo no denominador atribui ao
    // laboratório uma perda que aconteceu no curral.
    viabilidade:   taxa(viaveis, oocitos),
    clivagem:      taxa(clivados, viaveis),
    blastocisto:   taxa(embrioes, viaveis),
    transferencia: taxa(transferidos, embrioes),
    // Sobre o que foi CONFERIDO, não sobre tudo que foi transferido.
    // Com 76 transferidas e nenhuma conferência lançada, o denominador antigo
    // produzia "0% de prenhez" — que se lê como "todas falharam", quando a
    // verdade é "ninguém conferiu ainda". Sem conferência, a taxa é nula e a
    // tela mostra "—".
    prenhez30:     taxa(prenhes30, conferidas30),
    prenhez60:     taxa(prenhes60, conferidas60),
  };
};

/**
 * Filtros dos relatórios que não vão ao banco.
 *
 * Client-side de propósito: o conjunto já está em memória (centenas de linhas,
 * não milhares) e filtrar aqui responde na tecla, sem uma ida à rede por
 * caractere digitado.
 *
 * `includes` e sem caixa: quem procura o touro "HIBISCUS" digita "hibi", e
 * quem procura o projeto "1123" pode digitar só "112".
 *
 * Lê `bull_name` OU `touro` porque as views não concordam no nome — produção
 * usa o primeiro, saídas o segundo. Unificar as views é cirurgia maior; aceitar
 * os dois aqui custa uma linha.
 */
export const aplicarFiltrosLocais = (linhas = [], { sebraeProjeto, touro } = {}) => {
  let r = linhas || [];
  const sp = (sebraeProjeto || '').trim().toLowerCase();
  const t  = (touro || '').trim().toLowerCase();
  if (sp) r = r.filter(l => (l.sebrae_projeto || '').toLowerCase().includes(sp));
  if (t)  r = r.filter(l => (l.bull_name || l.touro || '').toLowerCase().includes(t));
  return r;
};
